import React, { useEffect, useState } from "react";
import { formatDate } from "@/lib/formatDate";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiClient } from "@/lib/apiClient";
import { ReceiptThumbnail } from "@/components/ReceiptThumbnail";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Trash2, ZoomIn } from "lucide-react";
import { DocCompareDialog } from "@/components/DocCompareDialog";

/** Merge-Dialog für Duplikat-Belege.
 *  Öffnet sich beim Klick auf den "Mögliches Duplikat"-Badge in der Belege-Liste.
 *  Zeigt alle Duplikate mit KI-Score und lässt den User den Behaltenden wählen.
 */
export function DuplicateMergerDialog({ receiptId, open, onClose, onMerged }) {
  const [loading, setLoading] = useState(false);
  const [group, setGroup] = useState(null);
  const [selectedKeep, setSelectedKeep] = useState(null);
  const [merging, setMerging] = useState(false);
  const [compare, setCompare] = useState(false);

  useEffect(() => {
    if (!open || !receiptId) return;
    setLoading(true);
    setGroup(null);
    setSelectedKeep(null);
    apiClient
      .get(`/receipts/${receiptId}/duplicates`)
      .then((r) => {
        setGroup(r.data);
        const rec = r.data.items.find((i) => i.is_recommended_keep);
        setSelectedKeep(rec?.id || r.data.items[0]?.id);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Duplikate konnten nicht geladen werden");
      })
      .finally(() => setLoading(false));
  }, [open, receiptId]);

  const doMerge = async (discardId) => {
    if (!selectedKeep || !discardId || selectedKeep === discardId) return;
    setMerging(true);
    try {
      const r = await apiClient.post("/receipts/merge", {
        keep_id: selectedKeep,
        discard_id: discardId,
      });
      toast.success(r.data.message || "Merge erfolgreich");
      onMerged?.();
      // Re-load remaining duplicates
      const rr = await apiClient.get(`/receipts/${selectedKeep}/duplicates`);
      setGroup(rr.data);
      if (rr.data.count <= 1) onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge fehlgeschlagen");
    } finally {
      setMerging(false);
    }
  };

  const items = group?.items || [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-5xl max-h-[85vh] overflow-y-auto"
        data-testid="duplicate-merger-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" />
            KI-Duplikat-Merger
            {group && (
              <Badge variant="outline" className="ml-2 border-amber-300 text-amber-700 bg-amber-50">
                {group.count} Duplikat(e)
              </Badge>
            )}
            {items.length > 1 && (
              <Button variant="outline" size="sm" className="ml-auto mr-6 h-7" onClick={() => setCompare(true)} data-testid="duplicate-merger-zoom-btn">
                <ZoomIn className="h-4 w-4 mr-1" /> Gross vergleichen
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="duplicate-merger-loading">
            Lade Duplikate…
          </div>
        )}

        {!loading && items.length <= 1 && (
          <div className="p-8 text-center text-sm text-slate-500">
            Keine weiteren Duplikate mehr — dieser Beleg ist bereits eindeutig. 🎉
          </div>
        )}

        {!loading && items.length > 1 && (
          <>
            <p className="text-sm text-slate-600 mb-3">
              Wähle den Beleg, den du behalten möchtest. Die anderen werden als
              „superseded&quot; markiert und aus der Belege-Liste ausgeblendet
              (bleiben aber im Audit-Log). Empfehlung: der Beleg mit dem
              höchsten KI-Score.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {items.map((it) => {
                const isKeep = it.id === selectedKeep;
                return (
                  <div
                    key={it.id}
                    className={`rounded-lg border p-3 flex gap-3 transition ${
                      isKeep
                        ? "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-300"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`duplicate-item-${it.id}`}
                  >
                    <ReceiptThumbnail
                      receipt={{
                        _id: it.id,
                        mime_type: it.has_photo ? "image/jpeg" : null,
                        file_path: it.has_photo ? "yes" : null,
                      }}
                      size={72}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium text-sm truncate">
                          {it.merchant_name || it.description || "(Beleg)"}
                        </div>
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${
                            it.is_recommended_keep
                              ? "border-emerald-300 text-emerald-700 bg-emerald-50"
                              : "border-slate-200 text-slate-500"
                          }`}
                          title="KI-Qualitäts-Score"
                        >
                          Score {it.quality_score}
                          {it.is_recommended_keep && " ⭐"}
                        </Badge>
                      </div>
                      <div className="text-xs text-slate-600 mt-1">
                        {it.date ? formatDate(it.date) : "Kein Datum"} · {it.amount.toFixed(2)}{" "}
                        {it.currency}
                        {it.tax_rate ? ` · MWST ${it.tax_rate}%` : ""}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        <Badge
                          variant="outline"
                          className={`text-[9px] ${
                            it.status === "booked"
                              ? "border-emerald-300 text-emerald-700 bg-emerald-50"
                              : "border-slate-200 text-slate-600"
                          }`}
                        >
                          {it.status}
                        </Badge>
                        {it.category && (
                          <Badge
                            variant="outline"
                            className="text-[9px] border-indigo-200 text-indigo-700 bg-indigo-50"
                          >
                            {it.category}
                          </Badge>
                        )}
                        {it.source === "whatsapp" && (
                          <Badge
                            variant="outline"
                            className="text-[9px] border-emerald-200 text-emerald-700 bg-emerald-50"
                          >
                            📱 WhatsApp
                          </Badge>
                        )}
                        {it.ocr_text_length > 20 && (
                          <Badge
                            variant="outline"
                            className="text-[9px] border-slate-200 text-slate-600"
                          >
                            OCR {it.ocr_text_length}Z.
                          </Badge>
                        )}
                      </div>
                      <div className="mt-2 flex gap-1.5">
                        {isKeep ? (
                          <Badge
                            className="bg-emerald-500 text-white text-[10px]"
                            data-testid={`duplicate-keeper-${it.id}`}
                          >
                            ✓ Wird behalten
                          </Badge>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-[11px] h-6 px-2"
                              onClick={() => setSelectedKeep(it.id)}
                              data-testid={`duplicate-select-keep-${it.id}`}
                            >
                              Diesen behalten
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-[11px] h-6 px-2 text-rose-700 hover:bg-rose-50"
                              disabled={merging || it.status === "booked"}
                              onClick={() => doMerge(it.id)}
                              title={
                                it.status === "booked"
                                  ? "Zuerst zurückbuchen"
                                  : "Diesen als Duplikat markieren"
                              }
                              data-testid={`duplicate-discard-${it.id}`}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Verwerfen
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {selectedKeep && items.length >= 2 && (
              <div className="mt-4 flex items-center justify-between rounded-md bg-slate-50 border border-slate-200 p-3">
                <div className="text-xs text-slate-600 flex items-center gap-1.5">
                  <ArrowRight className="h-3.5 w-3.5" />
                  Klick „Verwerfen&quot; auf einen der schlechteren Belege um zu mergen.
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  data-testid="duplicate-merger-close"
                >
                  Schliessen
                </Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
      <DocCompareDialog
        open={compare}
        onClose={() => setCompare(false)}
        title="Duplikat-Lupe — Belege im Vergleich"
        docs={items.slice(0, 3).map((it) => ({
          id: it.id, fetchUrl: `/receipts/${it.id}/file`, fileName: it.merchant_name || it.description,
          title: it.merchant_name || it.description || "(Beleg)", accent: it.id === selectedKeep,
          tag: it.id === selectedKeep ? "Behalten" : "Duplikat",
          subtitle: `${it.date || "—"} · ${it.currency} ${Number(it.amount || 0).toFixed(2)} · KI-Score ${it.quality_score ?? "—"}`,
        }))}
      />
    </Dialog>
  );
}

export default DuplicateMergerDialog;
